import React, { useState, useEffect, useRef } from 'react'
import { api } from '../utils/supabase'
import {
  Search, Phone, MapPin, User, Wifi, WifiOff,
  Circle, ChevronLeft, ChevronRight, X, Loader2,
  Download
} from 'lucide-react'
import Icon3D from '../components/Icon3D'

const LIMIT = 25

const CARRIER_TYPES = [
  { value: 'ALL',    label: 'Todas las compañías' },
  { value: 'TOKA',   label: '🔴 TOKA SIM'         },
  { value: 'OUI',    label: '🟡 OUI'               },
  { value: 'TELCEL', label: '🔵 Telcel'             },
  { value: 'ALLCE',  label: '🟣 ALLCE'             },
  { value: 'AT&T',   label: '🟦 AT&T'              },
  { value: 'NONE',   label: '⚪ Sin compañía'       },
]

const STATUS_TYPES = [
  { value: 'ALL',     label: 'Todos los estados' },
  { value: 'active',  label: '🟢 Online'          },
  { value: 'idle',    label: '🟡 Idle'             },
  { value: 'offline', label: '🔴 Offline'          },
]

const CARRIER_STYLE = {
  TOKA:   { label: 'TOKA',   cls: 'bg-red-500/20 text-red-400 border-red-500/30'          },
  OUI:    { label: 'OUI',    cls: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  TELCEL: { label: 'Telcel', cls: 'bg-blue-500/20 text-blue-400 border-blue-500/30'       },
  ALLCE:  { label: 'ALLCE',  cls: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  'AT&T': { label: 'AT&T',   cls: 'bg-blue-500/20 text-blue-400 border-blue-500/30'      },
}

function StatusBadge({ status }) {
  const map = {
    active:  { icon: <Wifi    className="w-3 h-3" />, cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
    online:  { icon: <Wifi    className="w-3 h-3" />, cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
    idle:    { icon: <Circle  className="w-3 h-3" />, cls: 'text-yellow-400  bg-yellow-500/10  border-yellow-500/30'  },
    offline: { icon: <WifiOff className="w-3 h-3" />, cls: 'text-red-400     bg-red-500/10     border-red-500/30'     },
  }
  const s = map[status?.toLowerCase()] || { icon: <Circle className="w-3 h-3" />, cls: 'text-gray-400 bg-gray-700 border-gray-600' }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${s.cls}`}>
      {s.icon} <span className="capitalize">{status || 'unknown'}</span>
    </span>
  )
}

function CarrierBadge({ carrier, plan, carrierStatus, prevPhone, prevCarrier, prevIccid }) {
  if (!carrier && !prevPhone) return <span className="text-xs text-gray-600">Sin compañía</span>
  
  const s = CARRIER_STYLE[carrier] || { label: carrier, cls: 'bg-gray-700 text-gray-400 border-gray-600' }
  const active = carrierStatus?.toLowerCase().includes('activ') || carrierStatus?.toLowerCase() === 'activo'
  
  return (
    <div className="space-y-1">
      {/* Carrier Actual */}
      {carrier && (
        <div className="space-y-0.5">
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] border font-bold uppercase tracking-tighter ${s.cls}`}>
            {s.label}
            {carrierStatus && (
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${active ? 'bg-emerald-400' : 'bg-gray-500'}`} />
            )}
          </span>
          {plan && <p className="text-[10px] text-gray-400 truncate max-w-[130px] leading-tight" title={plan}>{plan}</p>}
        </div>
      )}

      {/* Historial (Anterior) */}
      {prevPhone && (
        <div className="pt-1 mt-1 border-t border-gray-700/50 flex flex-col gap-0.5 opacity-60 hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-1 text-[9px] font-medium text-gray-500 uppercase tracking-widest">
            Anterior
          </div>
          <div className="flex items-center gap-2">
            {prevCarrier && (
              <span className={`px-1 rounded text-[9px] font-bold border ${CARRIER_STYLE[prevCarrier]?.cls || 'bg-gray-800 text-gray-500 border-gray-700'}`}>
                {prevCarrier}
              </span>
            )}
            <span className="text-[10px] font-mono text-gray-400">{prevPhone}</span>
          </div>
          {prevIccid && <p className="text-[8px] font-mono text-gray-600 truncate max-w-[130px]">{prevIccid}</p>}
        </div>
      )}
    </div>
  )
}

export default function TrackerList() {
  const [trackers, setTrackers] = useState([])
  const [loading, setLoading]   = useState(true)
  const [search, setSearch]     = useState('')
  const [carrier, setCarrier]   = useState('ALL')
  const [status, setStatus]     = useState('ALL')
  const [page, setPage]         = useState(0)
  const [total, setTotal]       = useState(0)
  const debounceRef = useRef(null)

  const fetchTrackers = async (overrides = {}) => {
    setLoading(true)
    const p = {
      search:  search || undefined,
      carrier,
      status,
      limit:   LIMIT,
      offset:  page * LIMIT,
      ...overrides
    }
    try {
      const data = await api.get('/api/trackers', p)
      if (data.success) { setTrackers(data.data); setTotal(data.count || 0) }
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  useEffect(() => { fetchTrackers() }, [page, carrier, status])

  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => { setPage(0); fetchTrackers({ offset: 0 }) }, 350)
    return () => clearTimeout(debounceRef.current)
  }, [search])

  const pages = Math.ceil(total / LIMIT)

  const exportCSV = () => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (carrier && carrier !== 'ALL') params.set('carrier', carrier)
    if (status && status !== 'ALL') params.set('status', status)
    
    const url = `/api/trackers/export/csv?${params.toString()}`
    window.open(url, '_blank')
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Icon3D icon={MapPin} color="blue" size="md" /> Trackers GPS
          </h1>
          <p className="text-gray-400 text-sm">{total.toLocaleString()} registros</p>
        </div>
        <button
          onClick={exportCSV}
          disabled={loading || total === 0}
          className="btn btn-success"
        >
          <Download className="w-4 h-4" />
          Exportar CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Nombre, IMEI, teléfono, cliente..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input input-icon"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <select value={carrier} onChange={e => { setCarrier(e.target.value); setPage(0) }}
          className="input cursor-pointer">
          {CARRIER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(0) }}
          className="input cursor-pointer">
          {STATUS_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="table-wrap">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-7 h-7 text-amber-400 animate-spin" />
          </div>
        ) : trackers.length === 0 ? (
          <div className="text-center py-16 text-gray-500">Sin resultados</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="th">Unidad</th>
                  <th className="th">Estado</th>
                  <th className="th">Teléfono</th>
                  <th className="th">Compañía</th>
                  <th className="th">Cliente</th>
                  <th className="th hidden xl:table-cell">Modelo</th>
                  <th className="th hidden xl:table-cell">Última conexión</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {trackers.map(t => (
                  <tr key={t.id} className="row-hover">
                    {/* Unidad */}
                    <td className="td">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-gray-700 flex items-center justify-center shrink-0">
                          <MapPin className="w-3.5 h-3.5 text-amber-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-white text-sm truncate max-w-[140px]">{t.label || '—'}</p>
                          <p className="text-xs text-gray-500 font-mono">{t.external_id}</p>
                        </div>
                      </div>
                    </td>

                    {/* Estado */}
                    <td className="td">
                      <StatusBadge status={t.connection_status} />
                    </td>

                    {/* Teléfono */}
                    <td className="td">
                      {t.phone
                        ? <div className="flex items-center gap-1 text-sm font-mono text-white">
                            <Phone className="w-3 h-3 text-gray-500 shrink-0" />
                            {t.phone}
                          </div>
                        : <span className="text-gray-600 text-xs">Sin teléfono</span>
                      }
                    </td>

                    {/* Compañía — cruzada con toka/oui/telcel */}
                    <td className="td">
                      <CarrierBadge
                        carrier={t.carrier}
                        plan={t.carrier_plan}
                        carrierStatus={t.carrier_status}
                        prevPhone={t.prev_phone}
                        prevCarrier={t.prev_carrier}
                        prevIccid={t.prev_iccid}
                      />
                    </td>

                    {/* Cliente */}
                    <td className="td">
                      <div className="flex items-center gap-1.5 text-sm text-gray-300">
                        <User className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                        <span className="truncate max-w-[140px]">{t.client_name || '—'}</span>
                      </div>
                    </td>

                    {/* Modelo */}
                    <td className="px-4 py-3 hidden xl:table-cell text-sm text-gray-400">
                      {t.model_name || '—'}
                    </td>

                    {/* Última conexión */}
                    <td className="px-4 py-3 hidden xl:table-cell text-xs text-gray-500">
                      {t.last_connection
                        ? new Date(t.last_connection).toLocaleString('es-MX', {
                            day: '2-digit', month: 'short',
                            hour: '2-digit', minute: '2-digit'
                          })
                        : 'Nunca'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 text-sm">
            <span className="text-gray-400">
              {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} de {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => p - 1)} disabled={page === 0}
                className="btn btn-ghost !p-1.5">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-gray-400 min-w-[60px] text-center">{page + 1} / {pages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= pages - 1}
                className="btn btn-ghost !p-1.5">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
